import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  LightBulbIcon,
  BookOpenIcon,
  ClockIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import api from '../../config/api';
import { toast } from 'react-hot-toast';

interface WeakPoint {
  knowledgePointId: string;
  name: string;
  errorCount: number;
  correctRate: number;
  recentTrend: 'improving' | 'stable' | 'declining';
}

interface TrendData {
  period: string;
  totalQuestions: number;
  correctCount: number;
  correctRate: number;
  trend: string;
  improvement: number;
  weeklyData: {
    week: string;
    correctRate: number;
    questionCount: number;
  }[];
}

interface StudyPlan {
  priority: 'high' | 'medium' | 'low';
  knowledgePointId: string;
  knowledgePointName: string;
  suggestedActions: string[];
  estimatedTime: number;
}

interface LearningAnalysisData {
  weakPoints?: WeakPoint[];
  progressTrend?: string;
  overallMastery?: number;
  aiRecommendations?: string;
  studyPlan?: StudyPlan[];
  totalQuestionsAnswered?: number;
  totalCorrect?: number;
  totalWrong?: number;
}

const LearningAnalysis: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [weakPoints, setWeakPoints] = useState<WeakPoint[]>([]);
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [studyPlan, setStudyPlan] = useState<LearningAnalysisData | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [generating, setGenerating] = useState(false);

  // 加载数据
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [weakRes, trendRes] = await Promise.all([
          api.get('/error-analysis/weak-points'),
          api.get('/error-analysis/trend', { params: { period: selectedPeriod } })
        ]);

        setWeakPoints(weakRes.data?.data || []);
        setTrendData(trendRes.data?.data || null);
      } catch (error) {
        console.error('加载分析数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod]);

  // 生成学习计划
  const handleGeneratePlan = async () => {
    setGenerating(true);
    try {
      const response = await api.get('/error-analysis/study-plan');
      setStudyPlan(response.data?.data || null);
      toast.success('学习计划生成成功');
    } catch (error) {
      toast.error('生成失败');
    } finally {
      setGenerating(false);
    }
  };

  // 趋势图标
  const getTrendIcon = (trend: string) => {
    if (trend === 'improving') {
      return <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />;
    } else if (trend === 'declining') {
      return <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />;
    }
    return <div className="w-5 h-5 bg-gray-300 rounded-full" />;
  };

  // 优先级颜色
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-3 text-gray-500">加载分析数据...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">学习分析</h1>
              <p className="text-indigo-100">AI 驱动的个性化学习分析</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 总览卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">总答题数</p>
            <p className="text-2xl font-bold text-gray-900">
              {trendData?.totalQuestions || 0}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">正确率</p>
            <p className="text-2xl font-bold text-gray-900">
              {(trendData?.correctRate || 0).toFixed(1)}%
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">进步趋势</p>
            <div className="flex items-center gap-2">
              {getTrendIcon(trendData?.trend || 'stable')}
              <span className={`text-lg font-bold ${
                trendData?.trend === 'improving' ? 'text-green-600' :
                trendData?.trend === 'declining' ? 'text-red-600' : 'text-gray-600'
              }`}>
                {trendData?.trend === 'improving' ? '进步中' :
                 trendData?.trend === 'declining' ? '需加油' : '稳定'}
              </span>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">薄弱知识点</p>
            <p className="text-2xl font-bold text-red-600">{weakPoints.length}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧：薄弱知识点 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 趋势图 */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">学习趋势</h2>
                <div className="flex gap-2">
                  {(['7d', '30d', '90d'] as const).map(period => (
                    <button
                      key={period}
                      onClick={() => setSelectedPeriod(period)}
                      className={`px-3 py-1 text-sm rounded-lg ${
                        selectedPeriod === period
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {period === '7d' ? '7天' : period === '30d' ? '30天' : '90天'}
                    </button>
                  ))}
                </div>
              </div>

              {/* 简单的条形图表示 */}
              {trendData?.weeklyData && trendData.weeklyData.length > 0 ? (
                <div className="space-y-3">
                  {trendData.weeklyData.map((week, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="w-20 text-xs text-gray-500">{week.week}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${Math.max(week.correctRate, 5)}%` }}
                        >
                          <span className="text-xs text-white font-medium">
                            {week.correctRate.toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <span className="w-16 text-xs text-gray-500 text-right">
                        {week.questionCount}题
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  暂无趋势数据
                </div>
              )}
            </div>

            {/* 薄弱知识点 */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                  薄弱知识点
                </h2>
                <Link
                  to="/student/wrong-answers"
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                >
                  查看错题本
                </Link>
              </div>

              {weakPoints.length > 0 ? (
                <div className="space-y-3">
                  {weakPoints.map((wp, index) => (
                    <div
                      key={wp.knowledgePointId}
                      className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                          index < 3 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{wp.name}</p>
                          <p className="text-sm text-gray-500">
                            错题 {wp.errorCount} 道 · 正确率 {wp.correctRate.toFixed(0)}%
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getTrendIcon(wp.recentTrend)}
                        <Link
                          to={`/student/knowledge/${wp.knowledgePointId}`}
                          className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
                        >
                          去复习
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <CheckCircleIcon className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-gray-500">太棒了！暂无明显薄弱知识点</p>
                </div>
              )}
            </div>
          </div>

          {/* 右侧：学习计划 */}
          <div className="space-y-6">
            {/* AI 学习建议 */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
              <div className="flex items-center gap-2 mb-4">
                <SparklesIcon className="w-5 h-5 text-indigo-600" />
                <h2 className="font-semibold text-indigo-900">AI 学习建议</h2>
              </div>

              {studyPlan?.aiRecommendations ? (
                <p className="text-gray-700 text-sm whitespace-pre-wrap">
                  {studyPlan.aiRecommendations}
                </p>
              ) : (
                <div className="text-center py-4">
                  <button
                    onClick={handleGeneratePlan}
                    disabled={generating}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    {generating ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        生成中...
                      </span>
                    ) : (
                      '生成个性化学习计划'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* 学习计划列表 */}
            {studyPlan?.studyPlan && studyPlan.studyPlan.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpenIcon className="w-5 h-5 text-indigo-600" />
                  个性化学习计划
                </h2>
                <div className="space-y-3">
                  {studyPlan.studyPlan.map((plan, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-xl border ${getPriorityColor(plan.priority)}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{plan.knowledgePointName}</span>
                        <span className="flex items-center gap-1 text-xs">
                          <ClockIcon className="w-3 h-3" />
                          {plan.estimatedTime}分钟
                        </span>
                      </div>
                      <ul className="space-y-1">
                        {plan.suggestedActions.map((action, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <LightBulbIcon className="w-4 h-4 flex-shrink-0 mt-0.5 opacity-60" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 快捷操作 */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4">快捷操作</h2>
              <div className="space-y-2">
                <Link
                  to="/student/wrong-answers"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <span className="text-gray-700">查看错题本</span>
                  <span className="text-red-600 font-medium">
                    {weakPoints.reduce((sum, wp) => sum + wp.errorCount, 0)} 道
                  </span>
                </Link>
                <Link
                  to="/student/knowledge"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <span className="text-gray-700">浏览知识库</span>
                  <span className="text-indigo-600">→</span>
                </Link>
                <Link
                  to="/student/exams"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <span className="text-gray-700">练习考试</span>
                  <span className="text-indigo-600">→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningAnalysis;
